/**
 * Draft sentiment entry: delegates to lazy-loaded NLP.js (@nlpjs) implementation
 * so Senticon lexicons stay off the critical path bundle.
 */

export type DraftSentiment = {
  label: string;
  summary: string;
  positiveHits: number;
  negativeHits: number;
  /** ISO 639-1 when guessed between supported languages */
  detectedLanguage?: 'en' | 'es';
};

function fallbackLexiconSentiment(text: string): DraftSentiment {
  const lower = text.toLowerCase();
  const positive = ['good', 'great', 'excelente', 'bueno', 'esperanza', 'éxito', 'gracias', 'hope', 'thank'];
  const negative = ['bad', 'crisis', 'malo', 'peor', 'miedo', 'failure', 'fear', 'loss', 'risk'];
  let positiveHits = 0;
  let negativeHits = 0;
  for (const w of positive) {
    if (lower.includes(w)) positiveHits++;
  }
  for (const w of negative) {
    if (lower.includes(w)) negativeHits++;
  }
  const lean = positiveHits - negativeHits;
  let label = 'Neutral / mixed';
  if (lean >= 2) label = 'Leans positive';
  else if (lean <= -2) label = 'Leans negative';
  return {
    label,
    summary: 'Fallback keyword scan (NLP.js unavailable).',
    positiveHits,
    negativeHits,
  };
}

/**
 * Score draft plain text: guess EN vs ES, then NLP.js sentiment for that locale.
 */
export async function analyzeDraftSentiment(text: string): Promise<DraftSentiment> {
  const trimmed = (text || '').trim();
  if (!trimmed) {
    return {
      label: 'Neutral / mixed',
      summary: 'Empty draft — nothing to score.',
      positiveHits: 0,
      negativeHits: 0,
    };
  }

  try {
    const { runNlpSentiment } = await import('./draftSentimentNlpImpl');
    return await runNlpSentiment(trimmed);
  } catch {
    return fallbackLexiconSentiment(trimmed);
  }
}
